import React from 'react';

const HIGHLIGHT_COLORS = [
  'bg-yellow-200 dark:bg-yellow-900/40',
  'bg-blue-200 dark:bg-blue-900/40',
  'bg-green-200 dark:bg-green-900/40',
  'bg-pink-200 dark:bg-pink-900/40',
  'bg-purple-200 dark:bg-purple-900/40',
];

export interface HighlightResult {
  jsx: React.ReactNode;
  matchCount: number;
}

/**
 * Highlights search query matches in text with multiple colors
 * @param text - The text to search within
 * @param query - The search query to highlight
 * @param useMultiColor - Whether to use different colors for different words
 * @returns Object with JSX and match count
 */
export const highlightText = (
  text: string, 
  query: string,
  useMultiColor: boolean = false
): React.ReactNode => {
  const result = highlightTextWithCount(text, query, useMultiColor);
  return result.jsx;
};

/**
 * Highlights text and returns match count
 */
export const highlightTextWithCount = (
  text: string,
  query: string,
  useMultiColor: boolean = false
): HighlightResult => {
  if (!query.trim()) {
    return { jsx: text, matchCount: 0 };
  }

  // Escape special regex characters in the query
  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  
  // Split query into words for multi-word highlighting
  const queryWords = escapedQuery.split(/\s+/).filter(word => word.length > 0);
  
  // Create regex pattern that matches any of the query words
  const pattern = new RegExp(`(${queryWords.join('|')})`, 'gi');
  
  // Split text into parts (matched and unmatched)
  const parts = text.split(pattern);
  
  let matchCount = 0;
  
  const jsx = (
    <>
      {parts.map((part, index) => {
        // Check if this part matches any query word
        const matchIndex = queryWords.findIndex(word => 
          new RegExp(`^${word}$`, 'i').test(part)
        );
        
        if (matchIndex !== -1) {
          matchCount++;
          const colorClass = useMultiColor 
            ? HIGHLIGHT_COLORS[matchIndex % HIGHLIGHT_COLORS.length]
            : HIGHLIGHT_COLORS[0];
          
          return (
            <mark
              key={index}
              className={`${colorClass} text-foreground font-semibold px-0.5 rounded`}
            >
              {part}
            </mark>
          );
        }
        
        return <React.Fragment key={index}>{part}</React.Fragment>;
      })}
    </>
  );
  
  return { jsx, matchCount };
};

/**
 * Extracts context snippet around matched text
 * @param text - Full text to search
 * @param query - Search query
 * @param contextLength - Number of characters to show around match
 * @returns Context snippet with ellipses
 */
export const extractContextSnippet = (
  text: string,
  query: string,
  contextLength: number = 100
): string => {
  if (!query.trim() || !text) {
    const cleanText = stripHtmlTags(text);
    return cleanText.substring(0, contextLength * 2) + '...';
  }
  
  const cleanText = stripHtmlTags(text);
  const lowerText = cleanText.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const words = lowerQuery.split(/\s+/);
  
  // Find first match of any query word
  let matchIndex = -1;
  for (const word of words) {
    matchIndex = lowerText.indexOf(word);
    if (matchIndex !== -1) break;
  }
  
  if (matchIndex === -1) {
    return cleanText.substring(0, contextLength * 2) + '...';
  }
  
  // Calculate start and end positions
  const start = Math.max(0, matchIndex - contextLength);
  const end = Math.min(cleanText.length, matchIndex + contextLength);
  
  let snippet = cleanText.substring(start, end);
  
  // Add ellipses
  if (start > 0) snippet = '...' + snippet;
  if (end < cleanText.length) snippet = snippet + '...';
  
  return snippet;
};

/**
 * Simple fuzzy matching - checks if words start similarly
 * @param text - Text to search in
 * @param query - Query to match
 * @param threshold - Minimum similarity (0-1)
 * @returns Whether it's a fuzzy match
 */
export const fuzzyMatch = (
  text: string,
  query: string,
  threshold: number = 0.7
): boolean => {
  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  
  // Direct match
  if (lowerText.includes(lowerQuery)) return true;
  
  // Check if query is similar to any word in text
  const textWords = lowerText.split(/\s+/);
  const queryWords = lowerQuery.split(/\s+/);
  
  for (const qWord of queryWords) {
    for (const tWord of textWords) {
      // Check prefix match (e.g., "secur" matches "security")
      if (tWord.startsWith(qWord) || qWord.startsWith(tWord)) {
        if (Math.min(tWord.length, qWord.length) / Math.max(tWord.length, qWord.length) >= threshold) {
          return true;
        }
      }
      
      // Check if they share significant portion
      let commonLength = 0;
      for (let i = 0; i < Math.min(tWord.length, qWord.length); i++) {
        if (tWord[i] === qWord[i]) commonLength++;
        else break;
      }
      
      if (commonLength >= 4 && commonLength / Math.max(tWord.length, qWord.length) >= threshold) {
        return true;
      }
    }
  }
  
  return false;
};

/**
 * Strips HTML tags and decodes HTML entities from text
 * @param html - HTML string to clean
 * @returns Plain text without HTML markup
 */
export const stripHtmlTags = (html: string): string => {
  if (!html) return '';
  
  // Create a temporary div to decode HTML entities
  const temp = document.createElement('div');
  temp.innerHTML = html;
  
  // Get text content (automatically strips tags and decodes entities)
  const text = temp.textContent || temp.innerText || '';
  
  // Clean up extra whitespace
  return text.replace(/\s+/g, ' ').trim();
};
